import React, { useRef, useState, useCallback, forwardRef, useImperativeHandle, useEffect, useMemo } from 'react';
import { View, Text, StyleSheet, Platform, PanResponder, Pressable, TextInput, Alert } from 'react-native';
import Svg, { Rect, Text as SvgText } from 'react-native-svg';
import Animated, {
  useSharedValue,
  useAnimatedStyle,
  withSpring,
  runOnJS,
} from 'react-native-reanimated';
import { Dialog, XStack, YStack } from 'tamagui';
import { MaterialIcons, Feather } from '@expo/vector-icons';
import { getDepartmentHierarchyLevel } from '../utils/departmentPositioning';
import { debounce } from 'lodash';

export interface DepartmentGroupingHandle {
  forceUpdate: () => void;
}

interface DepartmentGroupingProps {
  departmentGroups: Array<{
    name: string;
    users: any[];
    color: string;
    count: number;
  }>;
  userPositionsRef: React.MutableRefObject<Record<string, { x: number; y: number }>>;
  theme: boolean;
  zoomLevel?: number;
  onDepartmentDrag?: (departmentName: string, deltaX: number, deltaY: number) => void;
  onDepartmentDragEnd?: (departmentName: string) => void;
  onDepartmentRename?: (oldName: string, newName: string) => void;
  onDepartmentHierarchyChange?: (departmentName: string, hierarchyLevel: number) => void;
}

// Department grouping visualization component
export const DepartmentGrouping = forwardRef<DepartmentGroupingHandle, DepartmentGroupingProps>(({
  departmentGroups,
  userPositionsRef,
  theme,
  zoomLevel = 1,
  onDepartmentDrag,
  onDepartmentDragEnd,
  onDepartmentRename,
  onDepartmentHierarchyChange
}, ref) => {
  const componentMountedRef = useRef(true);
  const [updateTrigger, setUpdateTrigger] = useState(0); // Internal update trigger
  const [dialogOpen, setDialogOpen] = useState(false);
  const [selectedDepartment, setSelectedDepartment] = useState<typeof departmentGroups[0] | null>(null);
  const [departmentName, setDepartmentName] = useState('');
  const [hierarchyLevel, setHierarchyLevel] = useState(2);

  // Debounced update function to prevent excessive re-renders during dragging
  // Similar to LinkHandler pattern - debounce to 16ms (60fps) to prevent state updates from interrupting drag
  const debouncedUpdate = useMemo(
    () => debounce(() => {
      if (componentMountedRef.current) {
        setUpdateTrigger(prev => prev + 1);
      }
    }, 16), // 60fps update rate (16ms) - prevents state updates from interrupting drag
    []
  );

  // Handle imperative updates from parent component
  useImperativeHandle(ref, () => ({
    forceUpdate() {
      // Use debounced update to prevent state updates from interrupting drag
      debouncedUpdate();
    }
  }), [debouncedUpdate]);

  // Component cleanup on unmount
  useEffect(() => {
    componentMountedRef.current = true;
    return () => {
      componentMountedRef.current = false;
      debouncedUpdate.cancel(); // Cancel any pending debounced updates
    };
  }, [debouncedUpdate]);

  // Read positions directly from ref (like UserLinkHandler pattern)
  // updateTrigger state change causes re-render, and during re-render we read latest positions from ref
  // This ensures real-time updates without state that would interrupt drag movements
  const userPositions = userPositionsRef.current;
  
  if (!departmentGroups || departmentGroups.length === 0) return null;

  // Calculate overall bounding box for all departments
  const allPositions: Array<{ x: number; y: number }> = [];
  departmentGroups.forEach(department => {
    department.users.forEach(user => {
      const pos = userPositions[user._id];
      if (pos) allPositions.push(pos);
    });
  });

  if (allPositions.length === 0) return null;

  // Calculate overall bounds
  const overallMinX = Math.min(...allPositions.map(pos => pos.x)) - 100;
  const overallMaxX = Math.max(...allPositions.map(pos => pos.x + 200)) + 100;
  const overallMinY = Math.min(...allPositions.map(pos => pos.y)) - 100;
  const overallMaxY = Math.max(...allPositions.map(pos => pos.y + 120)) + 100;

  const overallWidth = overallMaxX - overallMinX;
  const overallHeight = overallMaxY - overallMinY;

  // On Android, cap SVG dimensions to prevent bitmap size crashes
  const maxDimension = Platform.OS === 'android' ? 2000 : 10000;
  const svgWidth = Math.min(overallWidth, maxDimension);
  const svgHeight = Math.min(overallHeight, maxDimension);

  // Handler for opening settings dialog
  const handleOpenSettings = useCallback((department: typeof departmentGroups[0]) => {
    setSelectedDepartment(department);
    setDepartmentName(department.name);
    setHierarchyLevel(getDepartmentHierarchyLevel(department.name));
    setDialogOpen(true);
  }, []);

  // Handler for saving department settings
  const handleSaveSettings = useCallback(() => {
    if (!selectedDepartment) return;

    // Prevent renaming "Unassigned" - it's a special case
    if (selectedDepartment.name === 'Unassigned' && departmentName.trim() !== 'Unassigned') {
      Alert.alert(
        'Cannot Rename',
        'The "Unassigned" department cannot be renamed as it is a special system department.',
        [{ text: 'OK' }]
      );
      return;
    }

    // Validate department name
    if (!departmentName || !departmentName.trim()) {
      Alert.alert('Invalid Name', 'Department name cannot be empty.', [{ text: 'OK' }]);
      return;
    }

    // Handle rename if name changed
    if (departmentName.trim() !== selectedDepartment.name && onDepartmentRename) {
      onDepartmentRename(selectedDepartment.name, departmentName.trim());
    }

    // Handle hierarchy level change (Unassigned always stays at level -1)
    const currentLevel = selectedDepartment.name === 'Unassigned' 
      ? -1 
      : getDepartmentHierarchyLevel(selectedDepartment.name);
    
    // Only allow hierarchy level change for non-Unassigned departments
    if (selectedDepartment.name !== 'Unassigned' && hierarchyLevel !== currentLevel && onDepartmentHierarchyChange) {
      onDepartmentHierarchyChange(selectedDepartment.name, hierarchyLevel);
    }

    setDialogOpen(false);
    setSelectedDepartment(null);
  }, [selectedDepartment, departmentName, hierarchyLevel, onDepartmentRename, onDepartmentHierarchyChange]);

  // Draggable Department Name Component
  const DraggableDepartmentName = React.memo(({ 
    department, 
    minX, 
    minY, 
    theme,
    zoomLevel,
    onDepartmentDrag,
    onDepartmentDragEnd,
    onOpenSettings
  }: { 
    department: typeof departmentGroups[0];
    minX: number;
    minY: number;
    theme: boolean;
    zoomLevel: number;
    onDepartmentDrag?: (departmentName: string, deltaX: number, deltaY: number) => void;
    onDepartmentDragEnd?: (departmentName: string) => void;
    onOpenSettings?: (department: typeof departmentGroups[0]) => void;
  }) => {
    // Use ref instead of state to avoid re-renders during drag
    const isDraggingRef = useRef(false);
    const startPosition = useRef({ x: 0, y: 0 });
    const lastDelta = useRef({ x: 0, y: 0 });
    const translationX = useSharedValue(0);
    const translationY = useSharedValue(0);
    const opacity = useSharedValue(1);

    const panResponder = PanResponder.create({
      onStartShouldSetPanResponder: (evt, gestureState) => {
        // Don't start drag if clicking on settings button area (right side)
        const { locationX } = evt.nativeEvent;
        const labelWidth = Math.max(150, department.name.length * 12);
        
        // Only allow drag if touch is on the label area (left side), not settings button
        return locationX < labelWidth;
      },
      onMoveShouldSetPanResponder: (evt, gestureState) => {
        // Only continue drag if we're already dragging (check ref)
        return isDraggingRef.current;
      },
      onPanResponderGrant: (event, gestureState) => {
        startPosition.current = { x: 0, y: 0 };
        lastDelta.current = { x: 0, y: 0 };
        translationX.value = 0;
        translationY.value = 0;
        opacity.value = 0.8; // Visual feedback without state update
        isDraggingRef.current = true;
      },
      onPanResponderMove: (event, gestureState) => {
        const { dx, dy } = gestureState;
        const deltaX = (dx - lastDelta.current.x) / zoomLevel;
        const deltaY = (dy - lastDelta.current.y) / zoomLevel;
        
        translationX.value = dx / zoomLevel;
        translationY.value = dy / zoomLevel;
        
        lastDelta.current = { x: dx, y: dy };
        
        if (onDepartmentDrag) {
          runOnJS(onDepartmentDrag)(department.name, deltaX, deltaY);
        }
      },
      onPanResponderRelease: () => {
        opacity.value = 1; // Reset opacity without state update
        translationX.value = withSpring(0);
        translationY.value = withSpring(0);
        lastDelta.current = { x: 0, y: 0 };
        isDraggingRef.current = false;
        
        if (onDepartmentDragEnd) {
          runOnJS(onDepartmentDragEnd)(department.name);
        }
      },
    });

    const animatedStyle = useAnimatedStyle(() => ({
      transform: [
        { translateX: translationX.value },
        { translateY: translationY.value },
      ],
      opacity: opacity.value,
    }));

    const labelWidth = Math.max(150, department.name.length * 12);
    const labelHeight = 25;

    const handleSettingsPress = useCallback((e: any) => {
      e.stopPropagation();
      e.preventDefault();
      if (onOpenSettings) {
        onOpenSettings(department);
      }
    }, [department, onOpenSettings]);

    return (
      <Animated.View
        style={[
          {
            position: 'absolute',
            left: minX + 10,
            top: minY - 25,
            width: labelWidth + 40, // Add space for settings button
            height: labelHeight,
            zIndex: 1000,
            flexDirection: 'row',
            alignItems: 'center',
            gap: 4,
          },
          animatedStyle,
        ]}
      >
        <Animated.View
          style={{
            flex: 1,
            justifyContent: 'center',
            paddingHorizontal: 10,
            borderRadius: 8,
            backgroundColor: department.color,
            height: labelHeight,
          }}
          {...panResponder.panHandlers}
        >
          <Text
            style={{
              fontSize: 14,
              fontWeight: '600',
              color: 'white',
              userSelect: 'none',
            }}
            numberOfLines={1}
          >
            {department.name} ({department.count})
          </Text>
        </Animated.View>
        <Pressable
          onPress={handleSettingsPress}
          style={{
            width: labelHeight,
            height: labelHeight,
            borderRadius: 8,
            backgroundColor: department.color,
            justifyContent: 'center',
            alignItems: 'center',
          }}
        >
          <MaterialIcons
            name="settings"
            size={14}
            color="white"
          />
        </Pressable>
      </Animated.View>
    );
  });

  return (
    <View style={StyleSheet.absoluteFillObject} pointerEvents="box-none">
      <Svg 
        width={svgWidth} 
        height={svgHeight}
        viewBox={`${overallMinX} ${overallMinY} ${overallWidth} ${overallHeight}`}
        style={{ position: 'absolute', left: overallMinX, top: overallMinY }}
        pointerEvents="none"
      >
        {departmentGroups.map((department, index) => {
          // Calculate bounding box for department users
          const departmentUserPositions = department.users
            .map(user => userPositions[user._id])
            .filter(pos => pos); // Filter out undefined positions

          if (departmentUserPositions.length === 0) return null;

          // Calculate bounds
          const minX = Math.min(...departmentUserPositions.map(pos => pos.x)) - 50;
          const maxX = Math.max(...departmentUserPositions.map(pos => pos.x + 200)) + 50;
          const minY = Math.min(...departmentUserPositions.map(pos => pos.y)) - 80;
          const maxY = Math.max(...departmentUserPositions.map(pos => pos.y + 120)) + 50;

          const width = maxX - minX;
          const height = maxY - minY;

          // Ensure minimum size for visibility
          const minWidth = 200;
          const minHeight = 100;
          const finalWidth = Math.max(width, minWidth);
          const finalHeight = Math.max(height, minHeight);

          return (
            <React.Fragment key={department.name}>
              {/* Department background */}
              <Rect
                x={minX}
                y={minY}
                width={finalWidth}
                height={finalHeight}
                fill={theme ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.1)'}
                stroke={department.color}
                strokeWidth={3}
                strokeDasharray="15,8"
                rx={16}
                ry={16}
              />

              {/* Department label background (visual only, actual draggable is separate) */}
              <Rect
                x={minX + 10}
                y={minY - 25}
                width={Math.max(150, department.name.length * 12)}
                height={25}
                fill={department.color}
                rx={8}
                ry={8}
              />

              {/* Department name (visual only, actual draggable is separate) */}
              <SvgText
                x={minX + 20}
                y={minY - 8}
                fontSize={14}
                fontWeight="600"
                fill="white"
                userSelect="none"
                textAnchor="start"
              >
                {department.name} ({department.count})
              </SvgText>
            </React.Fragment>
          );
        })}
      </Svg>
      
      {/* Draggable department name overlays */}
      {departmentGroups.map((department) => {
        const departmentUserPositions = department.users
          .map(user => userPositions[user._id])
          .filter(pos => pos);

        if (departmentUserPositions.length === 0) return null;

        const minX = Math.min(...departmentUserPositions.map(pos => pos.x)) - 50;
        const minY = Math.min(...departmentUserPositions.map(pos => pos.y)) - 80;

        return (
          <DraggableDepartmentName
            key={`draggable-${department.name}`}
            department={department}
            minX={minX}
            minY={minY}
            theme={theme}
            zoomLevel={zoomLevel}
            onDepartmentDrag={onDepartmentDrag}
            onDepartmentDragEnd={onDepartmentDragEnd}
            onOpenSettings={handleOpenSettings}
          />
        );
      })}

      {/* Department Settings Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <Dialog.Portal>
          <Dialog.Overlay
            animation="quick"
            opacity={0.6}
            style={{
              zIndex: 1000,
              backgroundColor: 'rgba(0, 0, 0, 0.4)',
              ...(Platform.OS === 'web' && {
                backdropFilter: 'blur(8px)',
              })
            }}
            enterStyle={{ opacity: 0 }}
            exitStyle={{ opacity: 0 }}
          />
          <Dialog.Content
            bordered
            elevate
            animation={[
              'quick',
              {
                opacity: {
                  overshootClamping: true,
                },
              },
            ]}
            enterStyle={{ x: 0, y: -20, opacity: 0, scale: 0.9 }}
            exitStyle={{ x: 0, y: 10, opacity: 0, scale: 0.95 }}
            x={0}
            scale={1}
            style={[
              {
                padding: 0,
                minWidth: 500,
                maxWidth: 600,
                maxHeight: "85%",
                borderRadius: 24,
                overflow: 'hidden',
                elevation: 12,
                shadowColor: '#000',
                shadowOffset: { width: 0, height: 12 },
                shadowOpacity: 0.15,
                shadowRadius: 32,
                backgroundColor: theme ? 'rgba(255, 255, 255, 0.98)' : 'rgba(30, 41, 59, 0.98)',
                borderWidth: 1,
                borderColor: theme ? 'rgba(0, 0, 0, 0.05)' : 'rgba(255, 255, 255, 0.1)',
              },
              Platform.OS === 'web' && {
                backdropFilter: 'blur(24px)',
                boxShadow: '0 12px 48px rgba(0,0,0,0.12)',
              }
            ]}
            opacity={1}
            y={0}
          >
            {/* Header */}
            <View style={{
              backgroundColor: theme ? '#f8fafc' : '#374151',
              paddingHorizontal: 24,
              paddingVertical: 20,
              borderBottomWidth: 1,
              borderBottomColor: theme ? '#e5e7eb' : '#4b5563',
            }}>
              <XStack alignItems="center" gap="$3">
                <View style={{
                  backgroundColor: theme ? "#f0f9ff" : "#0c4a6e",
                  borderRadius: 12,
                  padding: 10,
                }}>
                  <Feather
                    name="settings"
                    size={20}
                    color={theme ? "#0ea5e9" : "#38bdf8"}
                  />
                </View>
                <YStack flex={1}>
                  <Dialog.Title style={{
                    fontSize: 20,
                    fontWeight: '700',
                    color: theme ? '#1e293b' : '#f1f5f9',
                    marginBottom: 4,
                    margin: 0,
                  }}>
                    Department Settings
                  </Dialog.Title>
                  <Dialog.Description style={{
                    fontSize: 14,
                    color: theme ? '#64748b' : '#94a3b8',
                    fontWeight: '500',
                    margin: 0,
                  }}>
                    {selectedDepartment ? `Configure settings for ${selectedDepartment.name}` : 'Configure department name and hierarchy level'}
                  </Dialog.Description>
                </YStack>
              </XStack>
            </View>

            {/* Content */}
            <View style={{ padding: 24 }}>
              <YStack gap="$4">
                {/* Department Name */}
                <View style={{
                  backgroundColor: theme ? '#f8fafc' : '#374151',
                  borderRadius: 16,
                  padding: 20,
                  borderWidth: 1,
                  borderColor: theme ? '#e5e7eb' : '#4b5563',
                }}>
                  <YStack gap="$2">
                    <Text style={{
                      fontSize: 14,
                      fontWeight: '600',
                      color: theme ? '#374151' : '#f9fafb',
                      textTransform: 'uppercase',
                      letterSpacing: 0.5,
                      marginBottom: 8,
                    }}>
                      Department Name
                    </Text>
                    <TextInput
                      value={departmentName}
                      onChangeText={setDepartmentName}
                      placeholder="Enter department name..."
                      placeholderTextColor={theme ? '#9ca3af' : '#6b7280'}
                      editable={selectedDepartment?.name !== 'Unassigned'}
                      style={{
                        fontSize: 16,
                        padding: 12,
                        borderWidth: 1,
                        borderColor: theme ? '#e5e7eb' : '#4b5563',
                        borderRadius: 12,
                        backgroundColor: selectedDepartment?.name === 'Unassigned' 
                          ? (theme ? '#f3f4f6' : '#374151')
                          : (theme ? '#ffffff' : '#1f2937'),
                        color: theme ? '#1f2937' : '#f9fafb',
                        minHeight: 48,
                        fontWeight: '500',
                        opacity: selectedDepartment?.name === 'Unassigned' ? 0.6 : 1,
                      }}
                    />
                    {selectedDepartment?.name === 'Unassigned' && (
                      <Text style={{
                        fontSize: 12,
                        color: theme ? '#6b7280' : '#9ca3af',
                        marginTop: 4,
                        fontStyle: 'italic',
                      }}>
                        Unassigned department cannot be renamed
                      </Text>
                    )}
                  </YStack>
                </View>

                {/* Hierarchy Level */}
                <View style={{
                  backgroundColor: theme ? '#f8fafc' : '#374151',
                  borderRadius: 16,
                  padding: 20,
                  borderWidth: 1,
                  borderColor: theme ? '#e5e7eb' : '#4b5563',
                }}>
                  <YStack gap="$2">
                    <Text style={{
                      fontSize: 14,
                      fontWeight: '600',
                      color: theme ? '#374151' : '#f9fafb',
                      textTransform: 'uppercase',
                      letterSpacing: 0.5,
                      marginBottom: 8,
                    }}>
                      Hierarchy Level
                    </Text>
                    <View style={{
                      flexDirection: 'row',
                      gap: 8,
                      flexWrap: 'wrap',
                    }}>
                      {[
                        { level: 0, label: 'Executive', description: 'Top level' },
                        { level: 1, label: 'Management', description: 'Middle level' },
                        { level: 2, label: 'Support', description: 'Lower level' },
                      ].map((option) => {
                        const isUnassigned = selectedDepartment?.name === 'Unassigned';
                        const isDisabled = isUnassigned; // Unassigned always stays at special level
                        
                        return (
                          <Pressable
                            key={option.level}
                            onPress={() => !isDisabled && setHierarchyLevel(option.level)}
                            disabled={isDisabled}
                            style={{
                              flex: 1,
                              minWidth: 120,
                              padding: 16,
                              borderRadius: 12,
                              borderWidth: 2,
                              borderColor: hierarchyLevel === option.level
                                ? (theme ? '#3b82f6' : '#60a5fa')
                                : (theme ? '#e5e7eb' : '#4b5563'),
                              backgroundColor: hierarchyLevel === option.level
                                ? (theme ? '#eff6ff' : '#1e3a8a')
                                : (theme ? '#ffffff' : '#1f2937'),
                              opacity: isDisabled ? 0.5 : 1,
                              ...(Platform.OS === 'web' && {
                                cursor: isDisabled ? 'not-allowed' : 'pointer',
                                transition: 'all 0.2s ease',
                              }),
                            }}
                          >
                            <Text style={{
                              fontSize: 16,
                              fontWeight: '600',
                              color: hierarchyLevel === option.level
                                ? (theme ? '#3b82f6' : '#60a5fa')
                                : (theme ? '#1f2937' : '#f9fafb'),
                              marginBottom: 4,
                            }}>
                              {option.label}
                            </Text>
                            <Text style={{
                              fontSize: 12,
                              color: hierarchyLevel === option.level
                                ? (theme ? '#3b82f6' : '#60a5fa')
                                : (theme ? '#6b7280' : '#9ca3af'),
                            }}>
                              {option.description}
                            </Text>
                          </Pressable>
                        );
                      })}
                    </View>
                    {selectedDepartment?.name === 'Unassigned' && (
                      <Text style={{
                        fontSize: 12,
                        color: theme ? '#6b7280' : '#9ca3af',
                        marginTop: 8,
                        fontStyle: 'italic',
                      }}>
                        Unassigned department has a fixed hierarchy level
                      </Text>
                    )}
                  </YStack>
                </View>
              </YStack>
            </View>

            {/* Footer Actions */}
            <View style={{
              backgroundColor: theme ? '#f8fafc' : '#374151',
              paddingHorizontal: 24,
              paddingVertical: 16,
              borderTopWidth: 1,
              borderTopColor: theme ? '#e5e7eb' : '#4b5563',
            }}>
              <XStack gap="$3" justifyContent="flex-end">
                <Dialog.Close asChild>
                  <Pressable
                    onPress={() => {
                      setDialogOpen(false);
                      setSelectedDepartment(null);
                    }}
                    style={{
                      paddingHorizontal: 20,
                      paddingVertical: 12,
                      borderRadius: 12,
                      backgroundColor: theme ? '#f1f5f9' : '#4b5563',
                      borderWidth: 1,
                      borderColor: theme ? '#e2e8f0' : '#6b7280',
                      minWidth: 100,
                      alignItems: 'center',
                    }}
                  >
                    <Text style={{
                      color: theme ? '#64748b' : '#d1d5db',
                      fontWeight: '600',
                      fontSize: 14,
                    }}>
                      Cancel
                    </Text>
                  </Pressable>
                </Dialog.Close>
                <Dialog.Close asChild>
                  <Pressable
                    onPress={handleSaveSettings}
                    style={{
                      paddingHorizontal: 20,
                      paddingVertical: 12,
                      borderRadius: 12,
                      backgroundColor: theme ? '#3b82f6' : '#2563eb',
                      minWidth: 100,
                      alignItems: 'center',
                      elevation: 2,
                      shadowColor: '#3b82f6',
                      shadowOffset: { width: 0, height: 2 },
                      shadowOpacity: 0.2,
                      shadowRadius: 8,
                    }}
                  >
                    <Text style={{
                      color: '#ffffff',
                      fontWeight: '600',
                      fontSize: 14,
                    }}>
                      Save Changes
                    </Text>
                  </Pressable>
                </Dialog.Close>
              </XStack>
            </View>
          </Dialog.Content>
        </Dialog.Portal>
      </Dialog>
    </View>
  );
});

// Department legend component
export const DepartmentLegend: React.FC<{
  departmentGroups: Array<{
    name: string;
    color: string;
    count: number;
  }>;
  theme: boolean;
  position: 'bottom-left' | 'bottom-right' | 'top-left' | 'top-right';
}> = ({ departmentGroups, theme, position }) => {
  if (!departmentGroups || departmentGroups.length === 0) return null;

  return (
    <View style={[
      styles.legend,
      position === 'bottom-left' && {
        left: 20,
        bottom: 40,
      },
      position === 'bottom-right' && {
        right: 20,
        bottom: 40,
      },
      {
        backgroundColor: theme ? 'rgba(255, 255, 255, 0.9)' : 'rgba(0, 0, 0, 0.8)',
        borderColor: theme ? '#e2e8f0' : '#4a5568'
      }
    ]}>
      <Text style={[
        styles.legendTitle,
        { color: theme ? '#2d3748' : '#ffffff' }
      ]}>
        Departments
      </Text>
      {departmentGroups.map((department) => (
        <View key={department.name} style={styles.legendItem}>
          <View style={[
            styles.colorIndicator,
            { backgroundColor: department.color }
          ]} />
          <Text style={[
            styles.legendText,
            { color: theme ? '#4a5568' : '#e2e8f0' }
          ]}>
            {department.name} ({department.count})
          </Text>
        </View>
      ))}
    </View>
  );
};

const styles = StyleSheet.create({
  legend: {
    position: 'absolute',
    padding: 16,
    borderRadius: 12,
    borderWidth: 1,
    marginTop: 40,
    marginLeft: 40,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 4 },
    shadowOpacity: 0.1,
    shadowRadius: 8,
    elevation: 5,
    minWidth: 200,
    zIndex: 1000,
  },
  legendTitle: {
    fontSize: 16,
    fontWeight: '700',
    marginBottom: 12,
  },
  legendItem: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: 8,
  },
  colorIndicator: {
    width: 12,
    height: 12,
    borderRadius: 6,
    marginRight: 10,
  },
  legendText: {
    fontSize: 14,
    fontWeight: '500',
  },
});

export default DepartmentGrouping;
